import { fetchMedOmforsok } from './lazy-layer-data.js';
/* state/jobs-data.js — lazy-loader och cache för aktiva jobbannonser per
 * kommun.
 *
 * Hämtar ``data/_generated/jobs/kommun_jobs.json``, producerat dagligen av
 * ``scripts/build_jobs_snapshot.py`` (Arbetsförmedlingens JobSearch-API).
 *
 * Format::
 *
 *     {
 *       fetched_at: "2026-05-26T03:00:12+00:00",
 *       source:     "https://jobsearch.api.jobtechdev.se",
 *       kommuner: {
 *         "0180": { total: 6966, fields: [{name, count}, ...],
 *                   jev: {distans, hogskola, sasong, n} },   // andelar 0–1 (Jev, #33)
 *         ...
 *       }
 *     }
 *
 * Public API:
 *   await loadJobsData()              — fetch + cache, returnerar payload
 *   getJobsTotal(kod)                 — synkron, returnerar number|null
 *   getJobsFields(kod)                — synkron, returnerar Array|null
 *   getJobsFetchedAt()                — ISO-sträng eller null
 *   getJobsStats()                    — {min,max,count,valuesById} eller null
 *
 * Lyssna på ``window`` för eventet ``jobs-data-loaded`` om du vill
 * re-rendera när snapshot kommit (samma mönster som scb-data-loaded).
 */

const URL_ = '/data/_generated/jobs/kommun_jobs.json?v=2';

let cache    = null;
let promise  = null;

export async function loadJobsData() {
    if (cache) return cache;
    if (promise) return promise;

    promise = fetchMedOmforsok(URL_)
        .then((r) => {
            if (!r.ok) throw new Error(`jobs-data: HTTP ${r.status}`);
            return r.json();
        })
        .then((d) => {
            cache = d || { kommuner: {} };
            promise = null;
            try {
                window.dispatchEvent(new CustomEvent('jobs-data-loaded'));
            } catch { /* SSR / no-window */ }
            return cache;
        })
        .catch((err) => {
            promise = null;
            console.warn('[jobs-data]', err);
            cache = { kommuner: {}, error: String(err) };
            setTimeout((fel) => { if (cache === fel) cache = null; }, 3000, cache);   // nästa load försöker igen
            return cache;
        });
    return promise;
}

function _entry(kod) {
    if (!cache || !cache.kommuner) return null;
    return cache.kommuner[kod] || null;
}

/** Antal aktiva annonser för kommun, eller null om snapshot inte laddad / saknas. */
export function getJobsTotal(kod) {
    const e = _entry(kod);
    if (!e) return null;
    return typeof e.total === 'number' ? e.total : null;
}

/** Lista av {name, count} för topp-yrkesfält, eller null om inget. */
export function getJobsFields(kod) {
    const e = _entry(kod);
    if (!e || !Array.isArray(e.fields)) return null;
    return e.fields;
}

/** Andel (%) av kommunens annonser där Jev svarat ja på `falt` ('distans' |
 *  'hogskola' | 'sasong'), räknat på upp till 100 annonser per kommun
 *  (scripts/build_jobs_snapshot.py, docs/JEV_IDEER.md #33). null om fältet
 *  saknas — snapshoten byggd utan nyckel har det inte. */
export function getJobsJev(kod, falt) {
    const e = _entry(kod);
    const j = e && e.jev;
    const v = j && j[falt];
    return (typeof v === 'number' && j.n > 0) ? v * 100 : null;
}

/** ISO-datum när snapshot byggdes (för "uppdaterad YYYY-MM-DD"-text). */
export function getJobsFetchedAt() {
    return cache && cache.fetched_at ? cache.fetched_at : null;
}

/** Lazy-cached payload. Null tills loadJobsData() har resolvat. */
export function getJobsPayload() {
    return cache;
}

/**
 * Min/max/count över alla kommuner (för choropleth-range, sort, etc).
 * Returnerar null om snapshot inte laddad eller tom.
 */
export function getJobsStats() {
    if (!cache || !cache.kommuner) return null;
    let min = Infinity, max = -Infinity, count = 0;
    const valuesById = {};
    for (const [kod, e] of Object.entries(cache.kommuner)) {
        const v = e && typeof e.total === 'number' ? e.total : null;
        if (v == null || !Number.isFinite(v)) continue;
        valuesById[kod] = v;
        count++;
        if (v < min) min = v;
        if (v > max) max = v;
    }
    if (!count) return null;
    if (min === max) max = min + 1;
    return { min, max, count, valuesById };
}
